import { useMutation, useQuery } from "@tanstack/react-query";
import { ambulatoryVisitApiService } from "modules/Clinical/AmbulatoryVisit/api/ambulatoryVisit.api";
import { GetUserData } from "common/service/CommonService";

interface IAmbulatoryVisitService {
    studyid?: number,
    searchtext?: string,
    currentPage?: number,
    pageSize?: number,
    visitid?: number,
    subjectassignmentid?: number,
    subjectassessmentid?: number,
}

export const useAmbulatoryVisitService = ({ studyid, searchtext, currentPage, pageSize, visitid, subjectassignmentid, subjectassessmentid }: IAmbulatoryVisitService) => {

    const SelectAllAmbulatoryVisit = useQuery({
        queryKey: ["selectall-ambulatory-visit", studyid, searchtext, currentPage, pageSize],
        queryFn: () =>
            ambulatoryVisitApiService.selectallAmbulatoryVisit({
                studyid: studyid,
                searchtext: searchtext,
                currentpage: currentPage,
                pagesize: pageSize,
            }),
        enabled: !!studyid,
    });

    const SelectdataAmbulatoryVisit = useQuery({
        queryKey: ["selectdata-ambulatory-visit", studyid, visitid],
        queryFn: () =>
            ambulatoryVisitApiService.selectdataAmbulatoryVisit({ studyid, visitid }),
        enabled: !!studyid && !!visitid,
    });

    const assignSubjectsAmbulatoryVisit = useMutation({
        mutationFn: (payload: any) =>
            ambulatoryVisitApiService.assignSubjects(payload),
    });

    const insertAmbulatoryVisitStatusHistory = useMutation({
        mutationFn: (payload: any) =>
            ambulatoryVisitApiService.insertAmbulatoryVisitStatusHistory(payload),
    });

    const SelectAllAssignedSubjects = useQuery({
        queryKey: ["selectall-assigned-subjects", studyid, visitid, searchtext, currentPage, pageSize],
        queryFn: () => {
            return ambulatoryVisitApiService.selectAllAssignedSubjects({
                visitid: Number(visitid),
                searchtext: searchtext,
                currentpage: currentPage,
                pagesize: pageSize
            });
        },
        enabled: !!visitid,
    });

    const SelectAllEligibleSubjects = useQuery({
        queryKey: ["selectall-eligible-subjects", studyid, searchtext, currentPage, pageSize],
        queryFn: () => {
            return ambulatoryVisitApiService.selectAllEligibleSubjects({
                studyid: studyid,
                searchtext: searchtext,
                currentpage: currentPage,
                pagesize: pageSize
            });
        },
        enabled: !!studyid,
    });

    const checkInSubject = useMutation({
        mutationFn: (payload: any) =>
            ambulatoryVisitApiService.checkInSubject(payload),
    });

    const checkOutSubject = useMutation({
        mutationFn: (payload: any) =>
            ambulatoryVisitApiService.checkOutSubject(payload),
    });

    const updateSubjectDecision = useMutation({
        mutationFn: (payload: any) =>
            ambulatoryVisitApiService.updateSubjectDecision(payload),
    });

    const SelectdataAmbulatoryVisitActivityResult = useQuery({
        queryKey: ["selectdata-ambulatory-visit-activity-result", subjectassignmentid],
        queryFn: () => {
            return ambulatoryVisitApiService.selectdataAmbulatoryVisitActivityResult({
                visitsubjectassignmentid: Number(subjectassignmentid)
            });
        },
        enabled: !!subjectassignmentid,
    });

    const updateSubjectCRFResult = useMutation({
        mutationFn: (payload: any) => ambulatoryVisitApiService.updateSubjectCRFResult(payload),
    });

    const insertAmbulatoryVisitSubjectAssessment = useMutation({
        mutationFn: (payload: any) => ambulatoryVisitApiService.insertAmbulatoryVisitSubjectAssessment(payload),
    });

    const SelectAmbulatoryVisitSubjectAssessment = useQuery({
        queryKey: ["select-ambulatory-visit-subject-assessment", subjectassignmentid, subjectassessmentid],
        queryFn: () => {
            const payload: any = {};
            if (subjectassessmentid) {
                payload.subjectassessmentid = Number(subjectassessmentid);
            } else if (subjectassignmentid) {
                payload.visitsubjectassignmentid = Number(subjectassignmentid);
            }
            return ambulatoryVisitApiService.selectAmbulatoryVisitSubjectAssessment(payload);
        },
        enabled: Boolean(subjectassessmentid || subjectassignmentid),
    });

    const insertAmbulatoryVisitSubjectBloodCollection = useMutation({
        mutationFn: (payload: any) => ambulatoryVisitApiService.insertAmbulatoryVisitSubjectBloodCollection(payload),
    });

    const SelectAmbulatoryVisitSubjectBloodCollection = useQuery({
        queryKey: ["select-ambulatory-visit-subject-blood-collection", subjectassignmentid, subjectassessmentid],
        queryFn: () => {
            const payload: any = {};
            if (subjectassessmentid) {
                payload.subjectassessmentid = Number(subjectassessmentid);
            } else if (subjectassignmentid) {
                payload.visitsubjectassignmentid = Number(subjectassignmentid);
            }
            return ambulatoryVisitApiService.selectAmbulatoryVisitSubjectBloodCollection(payload);
        },
        enabled: Boolean(subjectassessmentid || subjectassignmentid),
    });

    return {
        SelectAllAmbulatoryVisit,
        SelectdataAmbulatoryVisit,
        assignSubjectsAmbulatoryVisit,
        SelectAllAssignedSubjects,
        insertAmbulatoryVisitStatusHistory,
        checkInSubject,
        checkOutSubject,
        updateSubjectDecision,
        SelectdataAmbulatoryVisitActivityResult,
        updateSubjectCRFResult,
        SelectAllEligibleSubjects,
        insertAmbulatoryVisitSubjectAssessment,
        SelectAmbulatoryVisitSubjectAssessment,
        insertAmbulatoryVisitSubjectBloodCollection,
        SelectAmbulatoryVisitSubjectBloodCollection,
    };
};
